import { useEffect, useMemo } from 'react';
import Icon from '@ant-design/icons';
import { Common } from '@stockbit/ui';
import useShareTradeModal from 'features/sharetrade/stores/useShareTradeModal';
import ShareSvg from 'global/Icons/ShareSvg';
import Image from 'global/Image';
import { trackAutoBuyOrderReceivedView } from 'lib/analytics/autoOrder';
import { clevertapOrderReceived } from 'lib/analytics/securities';
import shallow from 'zustand/shallow';
import { ORDER_TYPE } from 'constants/trading';
import {
  BuyOrderType,
  OrderAction,
  SellOrderType,
} from 'global/Securities/types/transaction';
import { trackOrderShareSelected } from 'features/sharetrade/analytics/shareTradeShareTracker';
import { OrderPlacedConfirmWrapper, ShareTradeButton } from './styled';
import tradingModalStore from './store';

const { Button, Flex, Text, Modal } = Common;

interface PreviewModalProps {
  visible: boolean;
  onSubmit: () => void;
  orderType: string;
}

function OrderPlacedModal({ visible, onSubmit, orderType }: PreviewModalProps) {
  const isAmend = useMemo(
    () =>
      orderType === ORDER_TYPE.AMEND_BUY || orderType === ORDER_TYPE.AMEND_SELL,
    [orderType],
  );

  const symbol = tradingModalStore((s) => s.symbol);

  const [shareOrderType, resetShareTradeModal] = useShareTradeModal(
    (s) => [s.orderType, s.resetShareTradeModal],
    shallow,
  );

  const isShowShareButton = useMemo(
    () =>
      !isAmend &&
      [BuyOrderType.LimitOrder, SellOrderType.LimitOrder].includes(
        shareOrderType,
      ),
    [isAmend, shareOrderType],
  );

  const [isShareTradeVisible, setIsShareTradeVisible, setSharedUsers] =
    useShareTradeModal(
      (s) => [s.isVisible, s.setIsVisible, s.setSharedUsers],
      shallow,
    );

  const isOrderModalVisible = useMemo(
    () => visible && !isShareTradeVisible,
    [visible, isShareTradeVisible],
  );

  const handleSubmit = () => {
    resetShareTradeModal();
    setSharedUsers({});
    onSubmit();
  };

  const handleShareTradeClick = () => {
    setIsShareTradeVisible(true);
    trackOrderShareSelected(orderType as OrderAction, symbol);
  };

  useEffect(() => {
    // don't track if the modal is visible and it is a bond transaction
    if (!visible || orderType === ORDER_TYPE.BOND) {
      return;
    }

    if (orderType) {
      clevertapOrderReceived({
        orderType,
      });

      trackAutoBuyOrderReceivedView();
    }
  }, [visible]);

  return (
    <Modal
      visible={isOrderModalVisible}
      closable={false}
      width="420px"
      centered
    >
      <Flex direction="column" align="center" padding="50px">
        <Image src="/icon/previewOrder-success.svg" alt="order-placed" />
        <Text family="bold" padding="5px">
          Order is Now Placed
        </Text>
      </Flex>

      <OrderPlacedConfirmWrapper justify="space-between" width="100%">
        {isShowShareButton && (
          <ShareTradeButton
            icon={<Icon component={ShareSvg} />}
            type="default"
            radius="4px"
            block
            height="44px"
            onClick={handleShareTradeClick}
          >
            <Text align="inherit" color="inherit" family="bold" size="14px">
              Share Trade
            </Text>
          </ShareTradeButton>
        )}
        <Button
          onClick={handleSubmit}
          type="primary"
          radius="4px"
          block
          height="44px"
        >
          <Text align="inherit" color="inherit" family="bold" size="14px">
            Done
          </Text>
        </Button>
      </OrderPlacedConfirmWrapper>
    </Modal>
  );
}

export default OrderPlacedModal;
